import { ApolloLink, HttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { print } from 'graphql/language/printer';
import { sha256 } from 'crypto-hash';
import { isBrowser } from '../util/ssr';

/**
 * Custom Apollo link which generates a unique `X-Cache-Key` header
 * based on the requested query and its variables.
 * If the preview variable is included in the request, the cache key will be omitted.
 *
 * @example useQuery(MY_QUERY, { context: { withCacheKey: true })
 */
const cacheKeyLink = setContext((operation, prevContext) => {
  const shouldGenerateCacheKey =
    !operation.variables.preview && prevContext.withCacheKey === true;

  if (!shouldGenerateCacheKey) {
    return prevContext;
  }

  return sha256(print(operation.query) + JSON.stringify(operation.variables))
    .then((hashedCacheKey) => ({
      ...prevContext,
      headers: { ...prevContext.headers, 'X-Cache-Key': hashedCacheKey }
    }))
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.error(
        'There was an issue generating the hashed cache key header.',
        e
      );
    });
});

/**
 * Custom Apollo link which adds an `x-operation-name` header
 * based on the operation name.
 * @link https://www.apollographql.com/docs/apollo-server/workflow/requests#post-requests
 */
const operationNameLink = setContext((operation, prevContext) => {
  if (!operation.operationName) {
    return prevContext;
  }

  return {
    ...prevContext,
    headers: {
      ...prevContext.headers,
      'x-operation-name': operation.operationName
    }
  };
});

/**
 * Custom split HTTP link that allows us to point to the Shopify Storefront GraphQL API or Bombas' GraphQL API.
 * By default, we point to Bombas' GraphQL API.
 *
 * @example useQuery(MY_QUERY, { context: { service: 'storefront' })
 */
const link = (storefront, bombas) => (ssrInfoLogger, localizeLink) =>
  ApolloLink.split(
    (op) => op.getContext().service === 'storefront',
    new HttpLink({
      uri: storefront.url,
      headers: {
        'X-Shopify-Storefront-Access-Token': storefront.token
      }
    }),
    from([
      ...(localizeLink ? [localizeLink] : []),
      cacheKeyLink,
      operationNameLink,
      ...(!isBrowser() && ssrInfoLogger ? [ssrInfoLogger] : []),
      new HttpLink({
        uri: bombas.url,
        fetch: (uri, options) => {
          const query = isBrowser() ? window.location.search : '';
          const queryParams = new URLSearchParams(query);
          // get debug mode from query params
          const debugMode = queryParams.get('debug') === 'true';

          return fetch(uri, {
            ...options,
            headers: {
              ...options.headers,
              ...(debugMode ? { 'X-Bombas-Debug': 'true' } : {})
            }
          });
        }
      })
    ])
  );

export { link };
export default {};
